import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import { DateRange } from '@factorialco/f0-react/dist/experimental'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Crown, Flag, Plus, Split } from '@factorialco/f0-react/icons/app'
import {
  ProcurementTypeItemFragment,
  ProcurementTypesAuthorFilterDocument,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useProcurementTypeActions } from './hooks/useProcurementTypeActions'

interface Props {
  onCreateProcurementType?: () => void
  onEditProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onToggleProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onDeleteProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onDuplicateProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onRestoreProcurementType: (procurementType: ProcurementTypeItemFragment) => void
}

const ProcurementTypesDataCollection = ({
  onCreateProcurementType,
  onEditProcurementType,
  onToggleProcurementType,
  onDeleteProcurementType,
  onDuplicateProcurementType,
  onRestoreProcurementType,
}: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const client = useApolloClient()
  const { getActions } = useProcurementTypeActions({
    onEditProcurementType: onEditProcurementType,
    onToggle: onToggleProcurementType,
    onDelete: onDeleteProcurementType,
    onDuplicate: onDuplicateProcurementType,
    onRestore: onRestoreProcurementType,
  })

  const getDataFilterAsRange = (
    assessor: DateRange | Date | undefined
  ): { between: [string, string] } | undefined => {
    if (!assessor) return undefined

    let startMoment: moment.Moment
    let endMoment: moment.Moment

    // Single Date
    if (assessor instanceof Date) {
      startMoment = moment(assessor).startOf('day')
      endMoment = moment(assessor).endOf('day')
    }
    // DateRange object
    else {
      startMoment = assessor.from ? moment(assessor.from).startOf('day') : moment().startOf('day')
      endMoment = assessor.to ? moment(assessor.to).endOf('day') : moment().endOf('day')
    }

    return {
      between: [
        i18nDateToString(startMoment, 'backend_datetime'),
        i18nDateToString(endMoment, 'backend_datetime'),
      ],
    }
  }

  const getStatusFilterVariables = (
    statusValues?: string[]
  ): { enabledFilter: { eq: boolean } | undefined; deletedOnly: boolean } => {
    if (!statusValues?.length) return { enabledFilter: undefined, deletedOnly: false }

    const hasEnabled = statusValues.includes('enabled')
    const hasDisabled = statusValues.includes('disabled')
    const hasDeleted = statusValues.includes('deleted')

    if (hasDeleted) return { enabledFilter: undefined, deletedOnly: true }

    if (hasEnabled && hasDisabled) return { enabledFilter: undefined, deletedOnly: false }
    if (hasEnabled) return { enabledFilter: { eq: true }, deletedOnly: false }
    if (hasDisabled) return { enabledFilter: { eq: false }, deletedOnly: false }

    return { enabledFilter: undefined, deletedOnly: false }
  }

  const source = useGraphqlDataCollectionSource({
    document: ProcurementTypesDataCollectionDocument,
    primaryActions: () => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.add'),
      icon: Plus,
      variant: 'default',
      onClick: onCreateProcurementType,
    }),
    mapVariables: ({ sortings, filters }) => {
      const { enabledFilter, deletedOnly } = getStatusFilterVariables(filters.status)

      return {
        companyId: company.id,
        sortOrder: sortings?.[0],
        deletedOnly,
        filter: {
          enabled: enabledFilter,
          updatedAt: getDataFilterAsRange(filters.updatedAt),
          createdAt: getDataFilterAsRange(filters.createdAt),
        },
        authorIds: filters.authorIds?.filter((author) => author !== null),
        includeSystemAuthor: filters.authorIds?.includes(null),
      }
    },
    mapData: (data) => data.procurement.typesConnection.nodes,
    mapPagination: (data) => ({
      total: data.procurement.typesConnection.totalCount ?? 0,
    }),
    itemUrl: (item) => {
      const searchParams = new URLSearchParams({ tab: 'form' })
      return (
        navigation.settings.procurement.requestType.id.fullPath({ id: item.id }) +
        '?' +
        searchParams.toString()
      )
    },
    filters: {
      status: {
        label: i18n.t(
          'procurement.purchase_requests.settings.data_collection.filters.status.label'
        ),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t(
                'procurement.purchase_requests.settings.data_collection.filters.status.options.enabled'
              ),
              value: 'enabled',
            },
            {
              label: i18n.t(
                'procurement.purchase_requests.settings.data_collection.filters.status.options.disabled'
              ),
              value: 'disabled',
            },
            {
              label: i18n.t(
                'procurement.purchase_requests.settings.data_collection.filters.status.options.archived'
              ),
              value: 'deleted',
            },
          ],
        },
      },
      authorIds: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.filters.author'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            // Get the authors from the database
            const { data } = await client.query({
              query: ProcurementTypesAuthorFilterDocument,
              variables: {
                companyId: company.id,
              },
              fetchPolicy: 'cache-first',
            })
            // Filter the authors to not have dupliicages
            const filteredAuthors = data.procurement.types
              // Reduce to transform the authors into a record of id and label and to prevent duplicates
              .reduce<Record<string, { label: string; value: number }>>((acc, type) => {
                if (!type.author) return acc
                const stringifiedId = type.author.id.toString()
                if (!(stringifiedId in acc)) {
                  acc[stringifiedId] = {
                    label: type.author.fullName,
                    value: type.author.id,
                  }
                }
                return acc
              }, {})
            // Return the authors as an array of options
            return [
              // Add the system author as the first option
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.data_collection.filters.system_author',
                  { name: company.name }
                ),
                value: null,
              },
              // Add the filtered authors as the second option
              ...Object.values(filteredAuthors),
            ]
          },
        },
      },
      updatedAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.filters.updated_at'),
        type: 'date',
        options: {
          mode: 'range',
          view: 'day',
        },
      },
      createdAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.filters.created_at'),
        type: 'date',
        options: {
          mode: 'range',
          view: 'day',
        },
      },
    },
    sortings: {
      enabled: {
        label: i18n.t(
          'procurement.purchase_requests.settings.data_collection.sortings.status.enabled'
        ),
      },
      authorId: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.sortings.author'),
        options: {
          cache: true,
        },
      },
      updatedAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.sortings.updated_at'),
      },
      createdAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.sortings.created_at'),
      },
    },

    itemActions: (item) => getActions(item),
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: ({ name }: { name: string }) => name,
            description: ({ description }: { description?: string | null }) => description ?? '',
            cardProperties: [
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.last_updated'
                ),
                tooltip: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.last_updated'
                ),
                icon: Calendar,
                render: ({ updatedAt }) => {
                  return {
                    type: 'text',
                    value: i18nDateToString(
                      stringToDate(updatedAt, 'backend_datetime'),
                      'ui_short_with_time'
                    ),
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.author'
                ),
                tooltip: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.author'
                ),
                icon: Crown,
                render: ({ author }) => {
                  if (!author)
                    return {
                      type: 'company',
                      value: {
                        name: i18n.t(
                          'procurement.purchase_requests.settings.types.card_properties.system_author',
                          { name: company.name }
                        ),
                        src: buildUrl(company.workspaceLogo),
                      },
                    }
                  return {
                    type: 'person',
                    value: {
                      firstName: author?.firstName ?? '',
                      lastName: author?.lastName ?? '',
                      src: buildUrl(author?.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.status'
                ),
                tooltip: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.status'
                ),
                icon: Flag,
                render: ({ enabled, deletedAt }) => {
                  if (deletedAt) {
                    return {
                      type: 'status',
                      value: {
                        status: 'critical',
                        label: i18n.t(
                          'procurement.purchase_requests.settings.types.card_properties.status_archived'
                        ),
                      },
                    }
                  }

                  return {
                    type: 'status',
                    value: {
                      status: enabled ? 'positive' : 'neutral',
                      label: enabled
                        ? i18n.t(
                            'procurement.purchase_requests.settings.types.card_properties.status_enabled'
                          )
                        : i18n.t(
                            'procurement.purchase_requests.settings.types.card_properties.status_disabled'
                          ),
                      variant: enabled ? 'positive' : 'neutral',
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.workflow_status'
                ),
                tooltip: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.workflow_status'
                ),
                icon: Split,
                render: ({ workflowsConnection, deletedAt }) => {
                  const isRunning =
                    workflowsConnection?.nodes?.[0] && !workflowsConnection.nodes[0].disabled

                  return {
                    type: 'status',
                    value: {
                      status: isRunning && !deletedAt ? 'positive' : 'neutral',
                      label:
                        isRunning && !deletedAt
                          ? i18n.t(
                              'procurement.purchase_requests.settings.types.card_properties.status_running'
                            )
                          : i18n.t(
                              'procurement.purchase_requests.settings.types.card_properties.status_innactive'
                            ),
                      variant: isRunning && !deletedAt ? 'positive' : 'neutral',
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}

export default ProcurementTypesDataCollection
